<h1>Manage Sessions</h1>
@defer (when gridView.length > 0) {
<kendo-grid
  class="table-wrapper"
  [kendoGridBinding]="gridView"
  [sortable]="true"
  [resizable]="true"
  [pageSize]="pageSize"
  [pageable]="true"
  [columnMenu]="{ filter: true }"
  [scrollable]="'none'"
  [kendoGridExpandDetailsBy]="expandDetailsBy"
  [(expandedDetailKeys)]="expandedDetailKeys"
  (edit)="editHandler($event)"
  (detailExpand)="onDetailExpand($event)"
  (cancel)="cancelHandler($event)">

  <ng-template kendoGridToolbarTemplate>
    <kendo-textbox
      [style.width.px]="350"
      placeholder="Search..."
    (valueChange)="onFilter($event)"></kendo-textbox>
    <!-- Info button -->
    <app-info-button-tooltip
      [tooltipText]="slEnabled() ? 'This search bar filters the following data/columns: Name, Model Name (ex. gpt-4o), Sensitivity Labels, Modified Date, and Created Date. The Date fields can be sorted in this format ##-## by using hyphens instead of /.' : 'This search bar filters the following data/columns: Name, Model Name (ex. gpt-4o), Modified Date, and Created Date. The Date fields can be sorted in this format ##-## by using hyphens instead of /.'"
      [tooltipPosition]="'above'"
      />
      <kendo-grid-spacer></kendo-grid-spacer>
      <button type="button" kendoButton (click)="createNewSession()" themeColor="secondary" [svgIcon]="plusIcon">
        Create session
      </button>
    </ng-template>

    <kendo-grid-column field="details.label" title="Name"></kendo-grid-column>

    @if (slEnabled()) {
    <kendo-grid-column field="sensitivityLabelsList" title="Sensitivity Label"></kendo-grid-column>
    }

    <kendo-grid-column
      field="modifiedDate"
      title="Modified Date"
      [width]="dateColumnWidth">
      <ng-template kendoGridCellTemplate let-dataItem>
        <span>{{ dataItem.modifiedDate | date:'short' }}</span>
      </ng-template>
    </kendo-grid-column>

    <kendo-grid-column
      field="createdDate"
      title="Created Date"
      [width]="dateColumnWidth">
      <ng-template kendoGridCellTemplate let-dataItem>
        <span>{{ dataItem.createdDate | date:'short' }}</span>
      </ng-template>
    </kendo-grid-column>

    <kendo-grid-command-column title="Actions" [width]="editColumnWidth">
      <ng-template kendoGridCellTemplate let-dataItem>
        <div class="actions-wrapper">
          <button matIconButton
            class="btn-open"
            aria-label="Open Session"
            #tooltip="matTooltip"
            matTooltip="Open"
            matTooltipPosition="above" (click)="openHandler(dataItem)">
            <mat-icon class="material-symbols-outlined icon-open" fontIcon="forum"/>
          </button>

          <button
            kendoButton
            (click)="editHandler(dataItem)"
            [svgIcon]="pencilIcon"
            fillMode="clear"
            #tooltip="matTooltip"
            [matTooltip]="(dataItem.details.lastStatus === SessionStatus.PROCESSING || dataItem.details.lastStatus === SessionStatus.PAUSED) ? actionButtonTooltipText : 'Edit'"
            matTooltipPosition="above"
            aria-label="Edit Session"
            [disabled]="dataItem.details.lastStatus === SessionStatus.PROCESSING || dataItem.details.lastStatus === SessionStatus.PAUSED"></button>
          <button
            kendoButton
            (click)="openDeleteDialog(dataItem)"
            [svgIcon]="trashIcon"
            fillMode="clear"
            #tooltip="matTooltip"
            [matTooltip]="(dataItem.details.lastStatus === SessionStatus.PROCESSING || dataItem.details.lastStatus === SessionStatus.PAUSED) ? actionButtonTooltipText : 'Delete'"
            matTooltipPosition="above"
            aria-label="Delete Session"
            [disabled]="dataItem.details.lastStatus === SessionStatus.PROCESSING || dataItem.details.lastStatus === SessionStatus.PAUSED"></button></div>
        </ng-template>
      </kendo-grid-command-column>

      <!-- Detail template -->
      <ng-template kendoGridDetailTemplate let-dataItem>
        <div class="details-wrapper">
          <section>
            <p><span>Meta</span></p>
            <p><span>ID:</span> <span>{{ dataItem.details.id }}</span></p>
            <p><span>Temperature:</span> <span>{{ dataItem.details.temperature }}</span></p>
            <p><span>Top P:</span> <span>{{ dataItem.details.topP }}</span></p>
            @if (dataItem.details.model && dataItem.details.model.accountName !== '') {
              <p><span>Model:</span> <span>{{ dataItem.details.model.accountName }}/{{ dataItem.details.model.model }}</span></p>
            }
          </section>
          <section>
            @if (dataItem.agents && dataItem.agents.length > 0) {
              <p><span>Agents</span></p>
              <ul>
                @for (agent of dataItem.agents; track agent) {
                  <li>
                    <span>{{ agent.name }}</span>
                  </li>
                }
              </ul>
            }
          </section>
        </div>
      </ng-template>

      <!-- Pager template -->
      <ng-template
        kendoPagerTemplate
        let-totalPages="totalPages"
        let-currentPage="currentPage">
        <kendo-pager-prev-buttons></kendo-pager-prev-buttons>
        <kendo-pager-numeric-buttons [buttonCount]="2"></kendo-pager-numeric-buttons>
        <kendo-pager-next-buttons></kendo-pager-next-buttons>
        <kendo-pager-input></kendo-pager-input>
        <kendo-pager-info></kendo-pager-info>
        <kendo-pager-page-sizes [pageSizes]="pageSizes"></kendo-pager-page-sizes>
      </ng-template>
    </kendo-grid>
} @placeholder {
  <!--
  This extra boilerplate is needed to work around a Kendo UI for Angular issue where the
  grid does not except the data properly when data is loaded asynchronously.  There could be some race conditions
  happening internally in the Kendo grid component that causes it to not render data correctly when the data is
  available at the time of initial rendering.  The fix is to defer the grid with the condition that the data has a length greater than 0.
  The placeholder block displays the grid when the data has no length.  We do this because the grid has a feature to show
  a "no data" message when there is no data, and we want to leverage that.  Plus, we have buttons on the top of the grid that
  should be visible even when there is no data.
  -->
  <kendo-grid
    class="table-wrapper"
    [kendoGridBinding]="gridView"
    [sortable]="true"
    [resizable]="true"
    [pageSize]="pageSize"
    [pageable]="true"
    [columnMenu]="{ filter: true }"
    [scrollable]="'none'"
    [kendoGridExpandDetailsBy]="expandDetailsBy"
    [(expandedDetailKeys)]="expandedDetailKeys"
    (edit)="editHandler($event)"
    (detailExpand)="onDetailExpand($event)"
    (cancel)="cancelHandler($event)">

    <ng-template kendoGridToolbarTemplate>
      <kendo-textbox
        [style.width.px]="350"
        placeholder="Search..."
        (valueChange)="onFilter($event)"></kendo-textbox>
      <!-- Info button -->
      <app-info-button-tooltip
        [tooltipText]="slEnabled() ? 'This search bar filters the following data/columns: Name, Model Name (ex. gpt-4o), Sensitivity Labels, Modified Date, and Created Date. The Date fields can be sorted in this format ##-## by using hyphens instead of /.' : 'This search bar filters the following data/columns: Name, Model Name (ex. gpt-4o), Modified Date, and Created Date. The Date fields can be sorted in this format ##-## by using hyphens instead of /.'"
        [tooltipPosition]="'above'"
      />
      <kendo-grid-spacer></kendo-grid-spacer>
      <button type="button" kendoButton (click)="createNewSession()" themeColor="secondary" [svgIcon]="plusIcon">
        Create session
      </button>
    </ng-template>

    <kendo-grid-column field="details.label" title="Name"></kendo-grid-column>

    @if (slEnabled()) {
    <kendo-grid-column field="sensitivityLabelsList" title="Sensitivity Label"></kendo-grid-column>
    }

    <kendo-grid-column
      field="modifiedDate"
      title="Modified Date"
      [width]="dateColumnWidth">
      <ng-template kendoGridCellTemplate let-dataItem>
        <span>{{ dataItem.modifiedDate | date:'short' }}</span>
      </ng-template>
    </kendo-grid-column>

    <kendo-grid-column
      field="createdDate"
      title="Created Date"
      [width]="dateColumnWidth">
      <ng-template kendoGridCellTemplate let-dataItem>
        <span>{{ dataItem.createdDate | date:'short' }}</span>
      </ng-template>
    </kendo-grid-column>

    <kendo-grid-command-column title="Actions" [width]="editColumnWidth">
      <ng-template kendoGridCellTemplate let-dataItem>
        <div class="actions-wrapper">
          <button matIconButton
                  class="btn-open"
                  aria-label="Open Session"
                  #tooltip="matTooltip"
                  matTooltip="Open"
                  matTooltipPosition="above" (click)="openHandler(dataItem)">
            <mat-icon class="material-symbols-outlined icon-open" fontIcon="forum"/>
          </button>

          <button
            kendoButton
            (click)="editHandler(dataItem)"
            [svgIcon]="pencilIcon"
            fillMode="clear"
            #tooltip="matTooltip"
            matTooltip="Edit"
            matTooltipPosition="above"
            aria-label="Edit Session"></button>
          <button
            kendoButton
            (click)="openDeleteDialog(dataItem)"
            [svgIcon]="trashIcon"
            fillMode="clear"
            #tooltip="matTooltip"
            matTooltip="Delete"
            matTooltipPosition="above"
            aria-label="Delete Session"></button></div>
      </ng-template>
    </kendo-grid-command-column>

    <!-- Detail template -->
    <ng-template kendoGridDetailTemplate let-dataItem>
      <div class="details-wrapper">
        <section>
          <p><span>Meta</span></p>
          <p><span>ID:</span> <span>{{ dataItem.details.id }}</span></p>
          <p><span>Temperature:</span> <span>{{ dataItem.details.temperature }}</span></p>
          <p><span>Top P:</span> <span>{{ dataItem.details.topP }}</span></p>
          @if (dataItem.details.model && dataItem.details.model.accountName !== '') {
            <p><span>Model:</span> <span>{{ dataItem.details.model.accountName }}/{{ dataItem.details.model.model }}</span></p>
          }
        </section>
        <section>
          @if (dataItem.agents && dataItem.agents.length > 0) {
            <p><span>Agents</span></p>
            <ul>
              @for (agent of dataItem.agents; track agent) {
                <li>
                  <span>{{ agent.name }}</span>
                </li>
              }
            </ul>
          }
        </section>
      </div>
    </ng-template>

    <!-- Pager template -->
    <ng-template
      kendoPagerTemplate
      let-totalPages="totalPages"
      let-currentPage="currentPage">
      <kendo-pager-prev-buttons></kendo-pager-prev-buttons>
      <kendo-pager-numeric-buttons [buttonCount]="2"></kendo-pager-numeric-buttons>
      <kendo-pager-next-buttons></kendo-pager-next-buttons>
      <kendo-pager-input></kendo-pager-input>
      <kendo-pager-info></kendo-pager-info>
      <kendo-pager-page-sizes [pageSizes]="pageSizes"></kendo-pager-page-sizes>
    </ng-template>
  </kendo-grid>
} @loading {
  <p>Loading sessions...</p>
} @error {
  <p>Failed to load sessions.</p>
}
